/** Default ambient playlist — pre-downloaded MP3s in Storage API */
const DEFAULT_SONGS = [
    { query: 'Nils Frahm Says', title: 'Nils Frahm Says', videoId: 'dIwwjy4slI8', storageUrl: 'https://api-storage.arkserver.arkturian.com/storage/media/35721', thumbnailUrl: 'https://api-storage.arkserver.arkturian.com/storage/media/35722', duration: 499 },
    { query: 'Ólafur Arnalds Near Light', title: 'Ólafur Arnalds Near Light', videoId: '0kYc55bXJFI', storageUrl: 'https://api-storage.arkserver.arkturian.com/storage/media/35723', thumbnailUrl: 'https://api-storage.arkserver.arkturian.com/storage/media/35724', duration: 209 },
    { query: 'Max Richter On the Nature of Daylight', title: 'Max Richter On the Nature of Daylight', videoId: 'rVN1B-tUpgs', storageUrl: 'https://api-storage.arkserver.arkturian.com/storage/media/35725', thumbnailUrl: 'https://api-storage.arkserver.arkturian.com/storage/media/35726', duration: 375 },
    { query: 'Bonobo Kiara', title: 'Bonobo Kiara', videoId: 'L-kyRh7N-kE', storageUrl: 'https://api-storage.arkserver.arkturian.com/storage/media/35727', thumbnailUrl: 'https://api-storage.arkserver.arkturian.com/storage/media/35728', duration: 231 },
    { query: 'Tycho Awake', title: 'Tycho Awake', videoId: '6XJBDX3Z0BY', storageUrl: 'https://api-storage.arkserver.arkturian.com/storage/media/35729', thumbnailUrl: 'https://api-storage.arkserver.arkturian.com/storage/media/35730', duration: 284 },
    { query: 'Hammock Breathturn', title: 'Hammock Breathturn', videoId: '1rENnKECnfs', storageUrl: 'https://api-storage.arkserver.arkturian.com/storage/media/35731', thumbnailUrl: 'https://api-storage.arkserver.arkturian.com/storage/media/35732', duration: 360 },
    { query: 'Helios Halving', title: 'Helios Halving', videoId: 'vnsWDQF1bcg', storageUrl: 'https://api-storage.arkserver.arkturian.com/storage/media/35734', thumbnailUrl: 'https://api-storage.arkserver.arkturian.com/storage/media/35735', duration: 327 },
    { query: 'Boards of Canada Dayvan Cowboy', title: 'Boards of Canada Dayvan Cowboy', videoId: 'Ng2vTXxnD20', storageUrl: 'https://api-storage.arkserver.arkturian.com/storage/media/35736', thumbnailUrl: 'https://api-storage.arkserver.arkturian.com/storage/media/35737', duration: 360 },
    { query: 'Jon Hopkins Open Eye Signal', title: 'Jon Hopkins Open Eye Signal', videoId: 'Q04ILDXe3QE', storageUrl: 'https://api-storage.arkserver.arkturian.com/storage/media/35738', thumbnailUrl: 'https://api-storage.arkserver.arkturian.com/storage/media/35739', duration: 482 },
    { query: 'Rival Consoles Howl', title: 'Rival Consoles Howl', videoId: 'Y7BV9YMM6bg', storageUrl: 'https://api-storage.arkserver.arkturian.com/storage/media/35740', thumbnailUrl: 'https://api-storage.arkserver.arkturian.com/storage/media/35741', duration: 363 },
];
export class MusicPlayer {
    constructor(_config, _bus, _queue) {
        this._config = _config;
        this._bus = _bus;
        this._queue = _queue;
        this._ctx = null;
        this._gain = null;
        this._currentSource = null;
        this._currentBuffer = null;
        this._playing = false;
        this._paused = false;
        this._pauseOffset = 0;
        this._startTime = 0;
        /** Pre-fetched next song (ready for gapless transition) */
        this._prefetched = null;
        this._prefetching = false;
        /**
         * Monotonic counter incremented on every stop/pause/skip. Any
         * in-flight loadAndPlay() that completes after one of these
         * lifecycle events fired will see a mismatch between the
         * generation it captured and the current one, and abort before
         * calling _playBuffer(). Without this, async fetches racing
         * against a user-triggered stop produced "music started after I
         * pressed stop" (real-device 2026-06-08).
         */
        this._loadGeneration = 0;
        /** Whether default playlist has been seeded */
        this._defaultSeeded = false;
        this._toolsApiBase = 'https://youtube-api.arkserver.arkturian.com';
    }
    /** Share an AudioContext with TtsPlayer for iOS compatibility */
    setAudioContext(ctx) {
        this._ctx = ctx;
        this._gain = ctx.createGain();
        this._gain.gain.value = MusicPlayer.NORMAL_LEVEL;
        this._gain.connect(ctx.destination);
    }
    get isPlaying() {
        return this._playing && !this._paused;
    }
    get isReady() {
        return this._ctx !== null && this._gain !== null;
    }
    /**
     * Seed the queue with pre-cached default songs and start playing
     * the first one immediately. No yt-search or MP3 download needed —
     * all songs are already in Storage API.
     */
    seedDefaultPlaylist() {
        if (this._defaultSeeded)
            return;
        this._defaultSeeded = true;
        // Shuffle and pick first 2 to play immediately, rest goes to queue
        const shuffled = [...DEFAULT_SONGS].sort(() => Math.random() - 0.5);
        for (const song of shuffled) {
            this._queue.enqueue({ query: song.query, videoId: song.videoId, title: song.title, source: 'default' });
            // Tag the queue item with pre-cached storage URL
            const items = this._queue.queue;
            const last = items[items.length - 1];
            if (last) {
                last.__storageUrl = song.storageUrl;
                last.__thumbnailUrl = song.thumbnailUrl;
                last.__duration = song.duration;
            }
        }
        this._bus.emit('log:message', {
            level: 'info',
            text: `♪ Default playlist: ${shuffled.length} pre-cached songs`,
            timestamp: Date.now(),
        });
        // Start playing the first song. Wait for AudioContext to be fully
        // resumed (iOS requires this after user gesture) then advance queue.
        const startFirstSong = async () => {
            // Ensure context is running
            if (this._ctx && this._ctx.state === 'suspended') {
                try {
                    await this._ctx.resume();
                }
                catch { }
            }
            this._bus.emit('log:message', {
                level: 'info',
                text: `♪ Starting playlist: ctx=${this._ctx?.state}, gain=${!!this._gain}, queue=${this._queue.queueLength}`,
                timestamp: Date.now(),
            });
            if (this._ctx && this._gain && this._queue.queueLength > 0) {
                this._advanceQueue();
            }
            else {
                this._bus.emit('log:message', {
                    level: 'warn',
                    text: `♪ Cannot start: ctx=${!!this._ctx}, gain=${!!this._gain}, queue=${this._queue.queueLength}`,
                    timestamp: Date.now(),
                });
            }
        };
        // Small delay to ensure primeAudio() has finished setting up AudioContext
        setTimeout(() => void startFirstSong(), 300);
    }
    /**
     * Load a song and play it. Checks in order:
     * 1. Pre-fetched buffer (instant)
     * 2. Pre-cached storage URL (fast — just fetch MP3, no yt-search/download)
     * 3. Full pipeline (yt-search → youtube/mp3 → storage → decode)
     */
    async loadAndPlay(query, storageUrl, thumbnailUrl, duration, source = 'ai') {
        if (!this._ctx || !this._gain) {
            this._bus.emit('log:message', {
                level: 'warn', text: 'MusicPlayer: no AudioContext', timestamp: Date.now(),
            });
            return;
        }
        // Capture lifecycle generation at start. If stop/pause/skip fires
        // during any of the async fetches below, this generation no longer
        // matches and we abort before _playBuffer() actually starts the
        // music. Without this guard, a user-pressed stop would silently
        // race against an in-flight song-fetch.
        const gen = this._loadGeneration;
        const stillCurrent = () => gen === this._loadGeneration;
        // 1. Check pre-fetched buffer (synchronous → no race window, but
        //    check anyway for symmetry)
        if (this._prefetched && this._prefetched.query === query) {
            if (!stillCurrent())
                return;
            const prepared = this._prefetched;
            this._prefetched = null;
            this._queue.setCurrent({ query: prepared.query, videoId: prepared.videoId, title: prepared.title, source });
            this._playBuffer(prepared.buffer);
            this._bus.emit('music:started', {
                title: prepared.title, videoId: prepared.videoId,
                duration: prepared.duration, prefetched: true,
                thumbnailUrl: prepared.thumbnailUrl,
            });
            this._prefetchNext();
            return;
        }
        // 2. Pre-cached storage URL (default playlist songs)
        if (storageUrl) {
            try {
                const prepared = await this._fetchFromStorage(query, storageUrl, thumbnailUrl, duration);
                if (!stillCurrent())
                    return;
                if (prepared) {
                    this._queue.setCurrent({ query: prepared.query, videoId: prepared.videoId, title: prepared.title, source });
                    this._playBuffer(prepared.buffer);
                    this._bus.emit('music:started', {
                        title: prepared.title, videoId: prepared.videoId,
                        duration: prepared.duration, prefetched: true,
                        thumbnailUrl: prepared.thumbnailUrl,
                    });
                    this._prefetchNext();
                    return;
                }
            }
            catch { }
        }
        // 3. Full pipeline
        const prepared = await this._fetchSong(query);
        if (!stillCurrent())
            return;
        if (!prepared) {
            this._advanceQueue();
            return;
        }
        this._queue.setCurrent({ query: prepared.query, videoId: prepared.videoId, title: prepared.title, source });
        this._playBuffer(prepared.buffer);
        this._bus.emit('music:started', {
            title: prepared.title, videoId: prepared.videoId,
            duration: prepared.duration, prefetched: false,
            thumbnailUrl: prepared.thumbnailUrl,
        });
        this._prefetchNext();
    }
    /** Fetch + decode a song directly from a Storage API URL (no yt-search needed) */
    async _fetchFromStorage(query, storageUrl, thumbnailUrl, duration) {
        this._bus.emit('log:message', {
            level: 'info', text: `♪ Loading from cache: "${query}"`, timestamp: Date.now(),
        });
        const audioRes = await fetch(storageUrl);
        if (!audioRes.ok)
            return null;
        const arrayBuf = await audioRes.arrayBuffer();
        if (this._ctx.state === 'suspended')
            await this._ctx.resume();
        const audioBuffer = await this._ctx.decodeAudioData(arrayBuf);
        return {
            query, title: query, videoId: '',
            buffer: audioBuffer,
            duration: duration || Math.round(audioBuffer.duration),
            thumbnailUrl,
        };
    }
    /**
     * Fetch + decode a song. Returns PreparedSong or null on failure.
     * Does NOT play — caller decides what to do with the buffer.
     */
    async _fetchSong(query) {
        this._bus.emit('log:message', {
            level: 'info', text: `♪ Fetching: "${query}"`, timestamp: Date.now(),
        });
        try {
            // Step 1: yt-search → videoId
            const searchRes = await fetch(`${this._config.cloudApiBootstrap}/api/yt-search?q=${encodeURIComponent(query)}`);
            if (!searchRes.ok)
                throw new Error(`yt-search HTTP ${searchRes.status}`);
            const searchData = await searchRes.json();
            const videoId = searchData?.videoId || searchData?.video_id;
            const title = searchData?.title || query;
            if (!videoId) {
                this._bus.emit('music:load-failed', { query, error: 'no yt-search result' });
                return null;
            }
            this._bus.emit('music:loading', { title, videoId });
            // Step 2: tools-api youtube/mp3 → storage_url
            const ytUrl = `https://www.youtube.com/watch?v=${videoId}`;
            const mp3Res = await fetch(`${this._toolsApiBase}/api/v1/youtube/mp3`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-API-Key': 'Inetpass1' },
                body: JSON.stringify({ url: ytUrl, quality: '128' }),
            });
            if (!mp3Res.ok) {
                const errBody = await mp3Res.text();
                throw new Error(`youtube/mp3 HTTP ${mp3Res.status}: ${errBody.slice(0, 200)}`);
            }
            const mp3Data = await mp3Res.json();
            if (!mp3Data.success || !mp3Data.storage_url) {
                throw new Error(`no storage_url: ${JSON.stringify(mp3Data).slice(0, 200)}`);
            }
            this._bus.emit('log:message', {
                level: 'info',
                text: `♪ MP3 ready: "${title}" (${mp3Data.cached ? 'cached' : 'fresh'}, ${Math.round((mp3Data.filesize || 0) / 1024)}KB)`,
                timestamp: Date.now(),
            });
            // Step 3: fetch MP3 → decode
            const audioRes = await fetch(mp3Data.storage_url);
            if (!audioRes.ok)
                throw new Error(`MP3 fetch HTTP ${audioRes.status}`);
            const arrayBuf = await audioRes.arrayBuffer();
            if (this._ctx.state === 'suspended') {
                await this._ctx.resume();
            }
            const audioBuffer = await this._ctx.decodeAudioData(arrayBuf);
            const duration = mp3Data.duration || Math.round(audioBuffer.duration);
            return { query, title, videoId, buffer: audioBuffer, duration };
        }
        catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            this._bus.emit('log:message', {
                level: 'error', text: `♪ Fetch error: ${msg}`, timestamp: Date.now(),
            });
            this._bus.emit('music:load-failed', { query, error: msg });
            return null;
        }
    }
    /** Pre-fetch the next song in the queue (background, non-blocking) */
    _prefetchNext() {
        if (this._prefetching || this._prefetched)
            return;
        const next = this._queue.peek();
        if (!next)
            return;
        this._prefetching = true;
        const storageUrl = next.__storageUrl;
        const thumbnailUrl = next.__thumbnailUrl;
        const duration = next.__duration;
        this._bus.emit('log:message', {
            level: 'info', text: `♪ Pre-fetching: "${next.query}"${storageUrl ? ' (cached)' : ''}`, timestamp: Date.now(),
        });
        const fetchPromise = storageUrl
            ? this._fetchFromStorage(next.query, storageUrl, thumbnailUrl, duration)
            : this._fetchSong(next.query);
        void fetchPromise.then((prepared) => {
            this._prefetching = false;
            if (prepared) {
                this._prefetched = prepared;
                this._bus.emit('log:message', {
                    level: 'info', text: `♪ Pre-fetched ready: "${prepared.title}"`, timestamp: Date.now(),
                });
            }
        }).catch(() => {
            this._prefetching = false;
        });
    }
    _playBuffer(buffer) {
        this._stopSource();
        this._currentBuffer = buffer;
        const source = this._ctx.createBufferSource();
        source.buffer = buffer;
        source.connect(this._gain);
        source.onended = () => {
            if (this._currentSource === source && !this._paused) {
                this._playing = false;
                this._currentSource = null;
                this._bus.emit('music:ended');
                this._advanceQueue();
            }
        };
        this._currentSource = source;
        this._playing = true;
        this._paused = false;
        this._pauseOffset = 0;
        this._startTime = this._ctx.currentTime;
        source.start(0);
    }
    /** Duck music volume for TTS overlay */
    duck(low) {
        if (!this._gain || !this._ctx)
            return;
        const target = low ? MusicPlayer.DUCK_LEVEL : MusicPlayer.NORMAL_LEVEL;
        const now = this._ctx.currentTime;
        this._gain.gain.cancelScheduledValues(now);
        this._gain.gain.setValueAtTime(this._gain.gain.value, now);
        this._gain.gain.linearRampToValueAtTime(target, now + MusicPlayer.DUCK_RAMP_MS / 1000);
    }
    pause() {
        // Always bump the load-generation: even if there's no song
        // currently playing, an in-flight loadAndPlay() must be told
        // to abort instead of starting music after the user pressed
        // pause/stop.
        this._loadGeneration++;
        if (!this._playing || this._paused || !this._currentSource || !this._ctx)
            return;
        this._pauseOffset += this._ctx.currentTime - this._startTime;
        this._stopSource();
        this._paused = true;
        this._bus.emit('music:paused');
    }
    resume() {
        if (!this._paused || !this._currentBuffer || !this._ctx || !this._gain)
            return;
        const source = this._ctx.createBufferSource();
        source.buffer = this._currentBuffer;
        source.connect(this._gain);
        source.onended = () => {
            if (this._currentSource === source && !this._paused) {
                this._playing = false;
                this._currentSource = null;
                this._bus.emit('music:ended');
                this._advanceQueue();
            }
        };
        this._currentSource = source;
        this._paused = false;
        this._startTime = this._ctx.currentTime;
        source.start(0, this._pauseOffset);
        this._bus.emit('music:resumed');
    }
    skip() {
        // Bump load-gen so an in-flight loadAndPlay (e.g. mid-fetch of
        // the song the user just skipped) doesn't start playing after
        // we've already advanced to the next track.
        this._loadGeneration++;
        this._stopSource();
        this._playing = false;
        this._paused = false;
        this._advanceQueue();
    }
    stop() {
        // Invalidate any in-flight loadAndPlay so it can't start
        // playing music after stop() returns.
        this._loadGeneration++;
        this._stopSource();
        this._playing = false;
        this._paused = false;
        this._currentBuffer = null;
        this._prefetched = null;
        this._prefetching = false;
        this._queue.setCurrent(null);
    }
    _stopSource() {
        if (this._currentSource) {
            try {
                this._currentSource.stop();
            }
            catch { }
            this._currentSource = null;
        }
    }
    _advanceQueue() {
        const next = this._queue.dequeue();
        if (!next) {
            this._queue.setCurrent(null);
            return;
        }
        // Extract pre-cached metadata if present (default playlist items)
        const storageUrl = next.__storageUrl;
        const thumbnailUrl = next.__thumbnailUrl;
        const duration = next.__duration;
        const itemSource = next.source ?? 'ai';
        if (this._prefetched && this._prefetched.query === next.query) {
            const prepared = this._prefetched;
            this._prefetched = null;
            this._queue.setCurrent({ query: prepared.query, videoId: prepared.videoId, title: prepared.title, source: itemSource });
            this._playBuffer(prepared.buffer);
            this._bus.emit('music:started', {
                title: prepared.title, videoId: prepared.videoId,
                duration: prepared.duration, prefetched: true,
                thumbnailUrl: prepared.thumbnailUrl,
            });
            this._prefetchNext();
        }
        else {
            this._prefetched = null;
            void this.loadAndPlay(next.query, storageUrl, thumbnailUrl, duration, itemSource);
        }
    }
    dispose() {
        this.stop();
        this._gain = null;
        this._ctx = null;
    }
}
MusicPlayer.DUCK_LEVEL = 0.15;
MusicPlayer.DUCK_RAMP_MS = 600;
MusicPlayer.NORMAL_LEVEL = 0.7;
//# sourceMappingURL=MusicPlayer.js.map